import React, { useState, useEffect, useCallback } from "react";
import "./style.scss";
import EmailIcon from "../../../assets/images/SignupImg/Email.svg";
import LockIcon from "../../../assets/images/SignupImg/Password.svg";
import EyeIcon from "../../../assets/icons/eye.svg";
import EyeSlashedIcon from "../../../assets/icons/eye-slashed.svg";
import CheckSolidGreenIcon from "../../../assets/icons/check-solid-green.svg";
import ArrowRightIcon from "../../../assets/icons/arrow-right.svg";
import GoogleIcon from "../../../assets/images/SignupImg/Google.svg";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import Spinner from "../../../reusableComponents/Spinner";
import { Checkbox } from "antd";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import {
  getUserProfile,
  sessionTimeout,
} from "../../../store/reducers/userProfileReducer";
import { RefftokenD } from "../../../store/actions/index";
import { AcctokenD, login, logout } from "../../../store/actions";
import { axiosInstance } from "../../../Axios";
import ErrorModal from "../../../reusableComponents/ErrorModal";
import { setExpiry } from "../../../store/reducers/tokenReducer";
import TwoFactorAuthModal from "../TwoFactorAuthModal";

// Validation schema
const SigninSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

function SigninForm({ setActiveTab }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isRememberMe, setIsRememberMe] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [combinedPUBody, setCombinedPUBody] = useState("");
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [loginEmail, setLoginEmail] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sessionTimeOut = useSelector((state) => state.user.sessionTimeOut);
  const store = useStore();

  useEffect(() => {
    if (sessionTimeOut) {
      setShowPopup(true);
      setCombinedPUBody("Please login again");
      dispatch(sessionTimeout(false));
    }
  }, [sessionTimeOut, dispatch]);

  const handleGoogleAuth = () => {
    window.location.href =
      "https://accounts.google.com/o/oauth2/v2/auth?scope=https://www.googleapis.com/auth/userinfo.email&include_granted_scopes=true&response_type=code&state=pass-through value&redirect_uri=https://app.alphacapitalgroup.uk/auth/google&client_id=402277231112-imkppquf33eo2ap9btus3lr7m74okdvh.apps.googleusercontent.com";
  };

  const toTitleCase = (str) => {
    return str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  };

  const initializeRefreshToken = useCallback(
    (refresh_token, expiresIn) => {
      const refreshTime = (Number(expiresIn) - 300) * 1000; // Refresh 5 minutes before expiry

      const currentRefreshToken = store.getState().REFFtoken;

      const refreshTimeout = setTimeout(() => {
        axiosInstance
          .post("get/refreshtoken/", { refresh_token })
          .then((res) => {
            const newExpiresIn = res.data.expiresIn || 3600;
            dispatch(RefftokenD(res.data.refreshToken));
            dispatch(AcctokenD(res.data.idToken));

            initializeRefreshToken(currentRefreshToken, newExpiresIn);
          })
          .catch((error) => {
            console.error("Error refreshing token:", error);
            dispatch(logout());
            setShowPopup(true);
            setCombinedPUBody("Your session has expired. Please log in again.");
          });
      }, refreshTime);

      return () => clearTimeout(refreshTimeout);
    },
    [dispatch, store]
  );

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsLoading(true);

    const data = {
      email: values.email,
      password: values.password,
      returnSecureToken: true,
    };

    try {
      setLoginEmail(values.email);
      const res = await axiosInstance.post("email/signin/", data);

      if (res.data.error) {
        setShowPopup(true);
        setCombinedPUBody(res.data.error.message);
        setIsLoading(false);
        return;
      }

      // Check if 2FA is required
      if (res.data.requires_2fa) {
        setTempToken(res.data.temp_token);
        setShow2FAModal(true);
        setIsLoading(false);
        return;
      }

      // Normal login flow (no 2FA required)
      dispatch(AcctokenD(res.data.idToken));
      dispatch(RefftokenD(res.data.refreshToken));

      const userProfileData = await dispatch(getUserProfile());

      if (
        Array.isArray(userProfileData["accounts"]) &&
        userProfileData["accounts"].length === 0
      ) {
        navigate("/FundingEvaluationPayment/89");
      } else if (
        Array.isArray(userProfileData["accounts"]) &&
        userProfileData["accounts"].length > 0
      ) {
        navigate("/AccountMetric");
      }

      const expiresInSeconds = 3600; // 1 hour
      const expiryDate = new Date();
      expiryDate.setSeconds(expiryDate.getSeconds() + expiresInSeconds);
      dispatch(setExpiry(expiryDate));

      if (isRememberMe) {
        initializeRefreshToken(res.data.refreshToken, expiresInSeconds);
      }

      dispatch(login());
    } catch (error) {
      setIsLoading(false);

      if (error.response && error.response.data && error.response.data.detail) {
        const errorMsg = toTitleCase(
          error.response.data.detail.replaceAll("_", " ")
        );
        setShowPopup(true);
        setCombinedPUBody(errorMsg);
      } else {
        setShowPopup(true);
        setCombinedPUBody("An unexpected error occurred. Please try again.");
      }

      dispatch(AcctokenD(0));
      dispatch(RefftokenD(0));
      dispatch(logout());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {showPopup && (
        <ErrorModal
          setModalOpen={setShowPopup}
          desc={combinedPUBody}
          btnText="Close"
        />
      )}
      
      {show2FAModal && (
        <TwoFactorAuthModal
          isOpen={show2FAModal}
          onClose={() => {
            setShow2FAModal(false);
            setTempToken("");
          }}
          email={loginEmail}
          tempToken={tempToken}
          isRememberMe={isRememberMe}
          expiresInSeconds={3600}
          onSuccess={() => {
            setShow2FAModal(false);
            setTempToken("");
          }}
        />
      )}
      
      <div className="signinform-container">
        <div className="signinform-subcontainer">
          <div>
            <div className="signinform-top-banner">
              <h2 className="signinform-title">Sign In</h2>
              <p className="signinform-subtitle">
                Welcome Back! Please enter your Details
              </p>
            </div>
            <Formik
              initialValues={{ email: "", password: "" }}
              validationSchema={SigninSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form className="signinform-innercontainer signinform-container_form">
                  <div className="signinform-subcontainer_form">
                    <div className="signinform-email-container">
                      <div className="signinform-innercontainer_form">
                        <div className="signinform-container_icon">
                          <img src={EmailIcon} alt="" className="signinform-icon" />
                        </div>
                        <label className="signinform-label" htmlFor="email">
                          Email Address
                        </label>
                        <div className="signinform-container_input">
                          <Field
                            type="text"
                            name="email"
                            id="email"
                            className={`signinform-input ${
                              errors.email && touched.email ? "input-error" : ""
                            }`}
                            placeholder="Enter Your E-Mail"
                            autoComplete="email"
                          />
                        </div>

                        {!errors.email && touched.email && (
                          <div className="signinform-container_icon">
                            <img
                              src={CheckSolidGreenIcon}
                              alt=""
                              className="signinform-icon"
                            />
                          </div>
                        )}
                      </div>
                      {errors.email && touched.email && (
                        <div className="error-message">{errors.email}</div>
                      )}
                    </div>
                  </div>

                  <div className="signinform-subcontainer_form">
                    <div className="signinform-password-container">
                      <div className="signinform-innercontainer_form">
                        <div className="signinform-container_icon">
                          <img src={LockIcon} alt="" className="signinform-icon" />
                        </div>
                        <label className="signinform-label" htmlFor="password">
                          Password
                        </label>
                        <div className="signinform-container_input">
                          <Field
                            type={isPasswordVisible ? "text" : "password"}
                            name="password"
                            id="password"
                            className={`signinform-input ${
                              errors.password && touched.password ? "input-error" : ""
                            }`}
                            placeholder="Enter Your Password"
                            autoComplete="current-password"
                          />
                        </div>
                        <div className="signinform-container_icon">
                          <img
                            src={isPasswordVisible ? EyeIcon : EyeSlashedIcon}
                            alt=""
                            className="signinform-icon"
                            onClick={() => setIsPasswordVisible((prev) => !prev)}
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                      </div>
                      {errors.password && touched.password && (
                        <div className="error-message">{errors.password}</div>
                      )}
                    </div>
                  </div>

                  <div className="forgotPasswordText">
                    <p onClick={() => setActiveTab("forgot-password")}>
                      Forgot Password?
                    </p>
                    <div className="remember_me">
                      <Checkbox onChange={(e) => setIsRememberMe(e.target.checked)}>
                        Remember me
                      </Checkbox>
                    </div>
                  </div>

                  <div className="signinform-container_button">
                    <button
                      type="submit"
                      className="signinform-button"
                      disabled={isLoading || isSubmitting}
                    >
                      {isLoading ? (
                        <Spinner />
                      ) : (
                        <>
                          Sign in
                          <img
                            src={ArrowRightIcon}
                            alt=""
                            className="signinform-icon"
                          />
                        </>
                      )}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>

          <div className="signup_with_google" onClick={handleGoogleAuth}>
            <p className="signinform-continue">Or sign in using</p>
            <div className="signinform-innercontainer signinform-container_icon">
              <div className="signinform-subcontainer_icon">
                <img
                  style={{ cursor: "pointer" }}
                  src={GoogleIcon}
                  alt="google logo"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SigninForm;
